import { InjectionToken, inject, Injectable, OnDestroy } from '@angular/core';
import { IcontestDataResponse, IcontestUser } from 'app/features/contest/models/contest-api.models';
import { GenderType } from 'app/features/contest/models/gender';
import { IUserInfo } from 'app/features/contest/models/contest-user-info.models';
import { delay, interval, Observable, of, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  DEFAULT_CONTEST_TEMPLATE,
  DEFAULT_CONTEST_TRACK_INFO,
  DEFAULT_CONTEST_USER_INFO,
  createContestResponse,
  createContestUser,
} from './contest-api.fixtures';
import {
  ContestApi,
  ContestDataMode,
  ContestDataQuery,
  DeleteActivityResponse,
} from './contest-api.types';

type MockContestApiConfig = {
  addUserEveryMs: number;
  initialUsers: number;
  latencyMs: number;
};

export const MOCK_CONTEST_API_CONFIG = new InjectionToken<MockContestApiConfig>(
  'MOCK_CONTEST_API_CONFIG',
  {
    factory: () => ({
      addUserEveryMs: 5000,
      initialUsers: 30,
      latencyMs: 150,
    }),
  }
);

const FIRST_NAMES: string[] = [
  'Alex',
  'Camille',
  'Chloe',
  'Jules',
  'Lea',
  'Lou',
  'Max',
  'Nina',
  'Noah',
  'Sam',
];

const LAST_NAMES: string[] = [
  'Bernard',
  'Dubois',
  'Durand',
  'Leroy',
  'Martin',
  'Moreau',
  'Petit',
  'Roux',
  'Simon',
  'Thomas',
];

@Injectable({ providedIn: 'root' })
export class MockContestApiService implements ContestApi, OnDestroy {
  private readonly config: MockContestApiConfig = inject(MOCK_CONTEST_API_CONFIG);
  private nextActivityId: number = 1;
  private participants: IcontestUser[] = Array.from({ length: this.config.initialUsers }, () =>
    this.createParticipant()
  );
  private interval: Observable<number> = interval(this.config.addUserEveryMs);
  private updateIntervalSouscription: Subscription | null = null;

  constructor() {
    this.updateIntervalSouscription = this.interval.subscribe(() => {
      this.participants = [this.createParticipant(), ...this.participants];
    });
  }

  ngOnDestroy(): void {
    this.updateIntervalSouscription?.unsubscribe();
  }

  deleteActivity(activityId: number): Observable<DeleteActivityResponse> {
    this.participants = this.participants.filter(
      (participant: IcontestUser) => participant.activity_id !== activityId
    );

    return this.respond({ message: 'Activity deleted.' });
  }

  exportData(
    _contestUsername: string,
    _hashId: string,
    _pprFilter: number,
    _email: string
  ): Observable<unknown> {
    void _contestUsername;
    void _hashId;
    void _pprFilter;
    void _email;
    return this.respond({ ok: true });
  }

  getContestData(query: ContestDataQuery): Observable<IcontestDataResponse> {
    const filteredParticipants: IcontestUser[] = this.filterByGender(
      this.participants,
      query.targetGender
    );
    const sortedParticipants: IcontestUser[] = this.sortParticipants(
      filteredParticipants,
      query.mode
    );
    const start: number = query.page * environment.contestInfo.userPerPage;
    const pageParticipants: IcontestUser[] = sortedParticipants
      .slice(start, start + environment.contestInfo.userPerPage)
      .map((participant: IcontestUser, index: number) => ({
        ...participant,
        ranking: index + 1,
      }));

    const total: number = sortedParticipants.length;
    const perPage: number = environment.contestInfo.userPerPage;
    const currentPage: number = query.page + 1;
    const lastPage: number = Math.max(1, Math.ceil(total / perPage));

    return this.respond(
      createContestResponse(pageParticipants, {
        links: {
          first: this.pageUrl(1),
          last: this.pageUrl(lastPage),
          next: currentPage < lastPage ? this.pageUrl(currentPage + 1) : null,
          prev: currentPage > 1 ? this.pageUrl(currentPage - 1) : null,
        },
        meta: {
          current_page: currentPage,
          from: pageParticipants.length > 0 ? start + 1 : null,
          last_page: lastPage,
          links: this.createPaginationLinks(currentPage, lastPage),
          path: this.pageUrl(null),
          per_page: perPage,
          to: pageParticipants.length > 0 ? start + pageParticipants.length : null,
          total,
        },
      })
    );
  }

  getUserInfo(username: string, _hashid: string): Observable<IUserInfo> {
    void _hashid;
    return this.respond({
      template: DEFAULT_CONTEST_TEMPLATE,
      track_infos: { ...DEFAULT_CONTEST_TRACK_INFO },
      unlocked_content: [],
      user_infos: {
        ...DEFAULT_CONTEST_USER_INFO.user_infos,
        activity_sessions_count: this.participants.length,
        avatar_url: `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(username)}`,
        full_name: username,
        username,
      },
    });
  }

  resetData(_contestUsername: string, _hashId: string): Observable<unknown> {
    void _contestUsername;
    void _hashId;
    this.nextActivityId = 1;
    this.participants = Array.from({ length: this.config.initialUsers }, () =>
      this.createParticipant()
    );
    return this.respond({ ok: true });
  }

  private createParticipant(): IcontestUser {
    const activityId: number = this.nextActivityId++;
    const completionPercentage: number = this.randomInt(20, 100);
    const chrono: number = this.randomInt(900, 7200);
    const firstName: string = this.pick(FIRST_NAMES);
    const lastName: string = this.pick(LAST_NAMES);

    return createContestUser({
      activity_id: activityId,
      age: String(this.randomInt(18, 70)),
      chrono,
      completion_percentage: completionPercentage,
      distance: Math.round((completionPercentage / 100) * DEFAULT_CONTEST_TRACK_INFO.track_length),
      email: `${firstName}.${lastName}.${activityId}@example.com`.toLowerCase(),
      first_name: firstName,
      gender: Math.random() > 0.5 ? 'male' : 'female',
      last_name: lastName,
      ppr: this.randomInt(100, 500),
      ranking: 0,
      start_date: Date.now() / 1000,
      wpk: Number((Math.random() * 3 + 1.5).toFixed(1)),
    });
  }

  private filterByGender(participants: IcontestUser[], targetGender: GenderType): IcontestUser[] {
    if (targetGender === 'maleDisplay') {
      return participants.filter((participant: IcontestUser) => participant.gender === 'male');
    }

    if (targetGender === 'femaleDisplay') {
      return participants.filter((participant: IcontestUser) => participant.gender === 'female');
    }

    return participants;
  }

  private pick(values: string[]): string {
    return values[this.randomInt(0, values.length - 1)];
  }

  private randomInt(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  private respond<T>(response: T): Observable<T> {
    return of(structuredClone(response)).pipe(delay(this.config.latencyMs));
  }

  private sortParticipants(participants: IcontestUser[], mode: ContestDataMode): IcontestUser[] {
    return [...participants].sort((a: IcontestUser, b: IcontestUser) => {
      switch (mode) {
        case 'last_activities':
          return b.start_date - a.start_date;
        case 'top_ranking_distance':
          return b.completion_percentage - a.completion_percentage;
        case 'top_ranking_ppr':
          return Number(b.ppr) - Number(a.ppr);
        case 'top_ranking_wpk':
          return Number(b.wpk) - Number(a.wpk);
        case 'top_ranking_chrono':
        default:
          return a.chrono - b.chrono;
      }
    });
  }

  private createPaginationLinks(currentPage: number, lastPage: number) {
    return [
      {
        active: false,
        label: '&laquo; Previous',
        page: currentPage > 1 ? currentPage - 1 : null,
        url: currentPage > 1 ? this.pageUrl(currentPage - 1) : null,
      },
      ...Array.from({ length: lastPage }, (_: unknown, index: number) => {
        const page: number = index + 1;
        return {
          active: page === currentPage,
          label: String(page),
          page,
          url: this.pageUrl(page),
        };
      }),
      {
        active: false,
        label: 'Next &raquo;',
        page: currentPage < lastPage ? currentPage + 1 : null,
        url: currentPage < lastPage ? this.pageUrl(currentPage + 1) : null,
      },
    ];
  }

  private pageUrl(page: null | number): string {
    const baseUrl: string = '/mock/contest/informations';

    return page === null ? baseUrl : `${baseUrl}?page=${page}`;
  }
}
